// ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
//  t478 (26-oct-2026) · VARIAS CUENTAS EN LA MISMA APP Y EN EL MISMO NAVEGADOR, COMO GMAIL
// ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «yo tengo la app de Biango con la clínica conectada, y por lo tanto no puedo tener 2 apps de Biango con la
//  cuenta de Superadmin y la cuenta de la clínica conectadas». Ahora en el menú de la cuenta: «Añadir otra cuenta» (se entra
//  con la otra, con su contraseña y su segundo factor, como siempre, sin cerrar la primera) y se pasa de una a otra con un
//  toque («Cuenta de la clínica · Cuenta de Biango»).
//
//  CÓMO, Y POR QUÉ ASÍ. La aplicación entera lee la sesión de UN sitio: `token` y `user` del almacén del navegador (lo mira
//  cada petición, services/api). En vez de enseñar a cuarenta sitios a elegir cuenta, hay UNA cuenta ACTIVA (la de esos dos
//  sitios, como siempre) y las demás DORMIDAS en este baúl (CLAVE_CUENTAS): su token, su usuario y cómo dejó su candado.
//  Pasar de una a otra lo hace una sola pantalla, pages/CambiarDeCuenta (ruta /cambiar-cuenta): duerme la activa, limpia la
//  sesión como un inicio de sesión (services/sesion · limpiarSesion), despierta la otra y RECARGA la aplicación. La recarga
//  es la garantía de que nada se mezcla: la caché de las pantallas, las conexiones en vivo y lo que hubiera en memoria son
//  de la cuenta anterior y se van con ella. Cada cuenta conserva su sesión, su caducidad (su token se renueva solo mientras
//  duerme: renovarLasDormidas) y su candado: al despertar, la pantalla de bloqueo mira cuánto lleva sin usarse ESA cuenta.
//
//  LOS AVISOS AL MÓVIL Y AL NAVEGADOR, DE TODAS: el dispositivo se apunta con la sesión de CADA cuenta (backend/routes/push.js,
//  con la lista de las demás abiertas para que no se suelten), y el aviso dice de qué cuenta es (`cuenta` en sus datos): al
//  tocarlo, la app pasa a esa cuenta (rutaParaAbrirAviso) y abre lo suyo.
//
//  Util sin imports: lo cargan de verdad las guardias que montan el menú, el registro del móvil y la app. Todo lo que toca el
//  almacén va con try/catch: sin almacén (modo privado, pruebas) no hay cuentas dormidas y todo funciona como siempre.

import { t as i18nT } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const CLAVE_CUENTAS = 'biango_cuentas_del_dispositivo'
export const MAX_CUENTAS = 5
export const RUTA_CAMBIAR = '/cambiar-cuenta'

const almacen = () => { try { return typeof localStorage !== 'undefined' ? localStorage : null } catch (_) { return null } }
const leerJson = (k) => { try { const a = almacen(); return a ? JSON.parse(a.getItem(k) || 'null') : null } catch (_) { return null } }
const idDe = (v) => { const n = parseInt(v, 10); return Number.isInteger(n) && n > 0 ? n : null }

// La cuenta activa (la de `user` y `token`): { id, user, token } o null.
export function cuentaActiva() {
  const a = almacen()
  if (!a) return null
  try {
    const token = a.getItem('token')
    const user = JSON.parse(a.getItem('user') || 'null')
    const id = idDe(user && user.id)
    return token && id ? { id, user, token } : null
  } catch (_) { return null }
}

// Las cuentas dormidas, tal y como están guardadas (sin la activa). Lo que no tenga forma, fuera.
export function cuentasDormidas() {
  const b = leerJson(CLAVE_CUENTAS)
  const lista = b && Array.isArray(b.dormidas) ? b.dormidas : []
  const activa = cuentaActiva()
  return lista.filter((c) => c && idDe(c.id) && typeof c.token === 'string' && c.token && c.user && (!activa || idDe(c.id) !== activa.id))
}
function guardarDormidas(lista) {
  const a = almacen()
  if (!a) return
  try {
    const limpia = (lista || []).slice(0, MAX_CUENTAS - 1)
    if (limpia.length) a.setItem(CLAVE_CUENTAS, JSON.stringify({ v: 1, dormidas: limpia }))
    else a.removeItem(CLAVE_CUENTAS)
  } catch (_) { /* sin almacén no hay cuentas dormidas */ }
}

// «Cuenta de Biango» para la del equipo de Biango (la clínica de la plataforma); si no, «Cuenta de <su clínica>».
export function etiquetaDe(u) {
  if (!u) return 'Cuenta'
  if (u.cuenta_de_plataforma === true) return i18nT('utils.cuentasDelDispositivo.cuenta_de_biango', 'Cuenta de Biango')
  const n = String(u.clinica_nombre || '').trim()
  return n ? 'Cuenta de ' + n : i18nT('utils.cuentasDelDispositivo.cuenta_de_la_clinica', 'Cuenta de la clínica')
}

// Lo que pinta el menú: la activa primero y después las dormidas. `caducada`: su token ya no vale (hay que volver a entrar).
export function cuentasParaElMenu(ahoraMs = Date.now()) {
  const out = []
  const act = cuentaActiva()
  if (act) out.push({ id: act.id, activa: true, etiqueta: etiquetaDe(act.user), nombre: nombreDe(act.user), email: act.user.email || '', caducada: false })
  for (const c of cuentasDormidas()) {
    out.push({ id: idDe(c.id), activa: false, etiqueta: etiquetaDe(c.user), nombre: nombreDe(c.user), email: (c.user && c.user.email) || '', caducada: tokenCaducado(c.token, ahoraMs) })
  }
  return out
}
const nombreDe = (u) => [u && u.nombre, u && u.apellidos].filter(Boolean).join(' ').trim() || (u && u.email) || ''

export function idsDeLasDormidas() { return cuentasDormidas().map((c) => idDe(c.id)) }
export function hayOtrasCuentas() { return cuentasDormidas().length > 0 }

// El `exp` de un token (sin verificar nada: solo para no mandar uno caducado).
function cargaDe(token) {
  try { return JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) || null } catch (_) { return null }
}
export function tokenCaducado(token, ahoraMs = Date.now()) {
  const c = cargaDe(token)
  return !c || !Number.isFinite(Number(c.exp)) || Number(c.exp) * 1000 <= ahoraMs + 60 * 1000
}

// ── DORMIR Y DESPERTAR (solo los llama pages/CambiarDeCuenta, que limpia la sesión entre medias) ─────────────────────
// `claves`: las del candado de la pantalla de bloqueo (services/sesion · CLAVE_BLOQUEO_MARCA / CLAVE_BLOQUEO_ACTIVO), que
// pasa quien llama para no escribirlas dos veces. Se guarda cuándo se usó por última vez ESTA cuenta y si estaba bloqueada.
export function dormirLaActiva(claves = {}) {
  const act = cuentaActiva()
  if (!act) return null
  const a = almacen()
  let actividad = null
  let bloqueada = false
  try {
    if (claves.marca) actividad = parseInt(a.getItem(claves.marca) || '0', 10) || null
    if (claves.activo) bloqueada = a.getItem(claves.activo) === String(act.id)
  } catch (_) { /* sin almacén */ }
  const entrada = { id: act.id, token: act.token, user: act.user, actividad, bloqueada, dormida_at: Date.now() }
  guardarDormidas([entrada, ...cuentasDormidas().filter((c) => idDe(c.id) !== act.id)])
  return entrada
}

// Saca una cuenta del baúl (sin ponerla todavía): la entrada, o null si no está.
export function sacarDormida(id) {
  const n = idDe(id)
  if (!n) return null
  const b = leerJson(CLAVE_CUENTAS)
  const lista = b && Array.isArray(b.dormidas) ? b.dormidas : []
  const c = lista.find((x) => x && idDe(x.id) === n)
  if (!c) return null
  guardarDormidas(lista.filter((x) => x && idDe(x.id) !== n))
  return c
}

// Pone una cuenta despertada como la activa, con su candado como lo dejó. Lo llama pages/CambiarDeCuenta DESPUÉS de limpiar
// la sesión anterior.
export function ponerComoActiva(c, claves = {}) {
  const a = almacen()
  if (!a || !c) return false
  try {
    a.setItem('user', JSON.stringify(c.user))
    a.setItem('token', c.token)
    if (claves.marca && c.actividad) a.setItem(claves.marca, String(c.actividad))
    if (claves.activo && c.bloqueada) a.setItem(claves.activo, String(c.id))
    return true
  } catch (_) { return false }
}

// Olvidar una cuenta dormida (la quita de este dispositivo) o, al entrar con una que ya estaba dormida, no tenerla dos veces.
export function olvidarDormida(id) {
  const n = idDe(id)
  if (!n) return
  const b = leerJson(CLAVE_CUENTAS)
  const lista = b && Array.isArray(b.dormidas) ? b.dormidas : []
  guardarDormidas(lista.filter((x) => x && idDe(x.id) !== n))
}

// Actualiza los datos de una cuenta dormida (su token renovado, su nombre).
function actualizarDormida(id, cambios) {
  const n = idDe(id)
  const b = leerJson(CLAVE_CUENTAS)
  const lista = b && Array.isArray(b.dormidas) ? b.dormidas : []
  guardarDormidas(lista.map((x) => (x && idDe(x.id) === n ? { ...x, ...cambios, user: { ...(x.user || {}), ...((cambios && cambios.user) || {}) } } : x)))
}

// ── LA RUTA QUE CAMBIA DE CUENTA ──────────────────────────────────────────────────────────────────────────────────────
// Solo rutas de la app: un `ir` con dominio propio o de doble barra no se sigue.
const rutaSegura = (r) => (typeof r === 'string' && /^\/[^/\\]/.test(r) ? r : '/')
export function rutaParaCambiar(id, ir = '/') {
  return RUTA_CAMBIAR + '?a=' + encodeURIComponent(String(idDe(id) || '')) + '&ir=' + encodeURIComponent(rutaSegura(ir))
}
export function rutaParaAnadir() { return RUTA_CAMBIAR + '?anadir=1' }

// AL TOCAR UN AVISO: si es de OTRA cuenta abierta en este dispositivo, la ruta que pasa a ella y después abre lo suyo;
// si es de esta (o de una que ya no está aquí), null y se abre como siempre.
export function rutaParaAbrirAviso(cuenta, ruta) {
  const n = idDe(cuenta)
  if (!n) return null
  const act = cuentaActiva()
  if (act && act.id === n) return null
  if (!cuentasDormidas().some((c) => idDe(c.id) === n)) return null
  return rutaParaCambiar(n, ruta)
}

// ── LOS AVISOS DE LAS CUENTAS DORMIDAS ────────────────────────────────────────────────────────────────────────────────
// El dispositivo se apunta también con la sesión de cada cuenta dormida (su token, nunca el de otra), diciendo qué otras
// siguen abiertas en él. Lo llaman el registro del móvil (push/registrarPush) y el del navegador (hooks/useNotifications)
// justo después de apuntar la activa. `base`: la de la API (en la app del móvil, la misma web).
export async function apuntarLasDormidas({ token, plataforma, dispositivo, base = '/api', _fetch } = {}) {
  const f = _fetch || (typeof fetch === 'function' ? fetch : null)
  if (!f || !token) return 0
  const act = cuentaActiva()
  const dormidas = cuentasDormidas()
  const todas = [...(act ? [act.id] : []), ...dormidas.map((c) => idDe(c.id))]
  let n = 0
  for (const c of dormidas) {
    if (tokenCaducado(c.token)) continue
    try {
      const r = await f(base + '/push/registrar', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + c.token, 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token, plataforma, dispositivo, cuentas: todas.filter((x) => x !== idDe(c.id)) }),
      })
      if (r && r.ok) n++
    } catch (_) { /* sin red: se vuelve a intentar al renovar */ }
  }
  return n
}

// Al cerrar UNA cuenta en este dispositivo: suelta su aviso (con SU sesión) y dice cuáles siguen, para no soltar las suyas.
export async function soltarAvisosDe({ tokenSesion, tokensDispositivo = [], siguen = [], base = '/api', _fetch } = {}) {
  const f = _fetch || (typeof fetch === 'function' ? fetch : null)
  if (!f || !tokenSesion) return 0
  let n = 0
  for (const t of tokensDispositivo.filter(Boolean)) {
    try {
      const r = await f(base + '/push/registrar', {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + tokenSesion, 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ token: t, siguen }),
      })
      if (r && r.ok) n++
    } catch (_) { /* la cuenta se cierra igual */ }
  }
  return n
}

// LAS DORMIDAS NO CADUCAN POR DORMIR. Su token dura 12 horas y se renueva solo mientras se usa (services/renovarSesion); una
// dormida no se usa, así que se renueva aquí, con SU token y por la misma puerta (POST /auth/renovar), dentro de su techo de
// 7 días desde que se tecleó su contraseña (el servidor no lo alarga). Una que ya ha caducado no se manda.
export async function renovarLasDormidas({ base = '/api', _fetch } = {}) {
  const f = _fetch || (typeof fetch === 'function' ? fetch : null)
  if (!f) return 0
  let n = 0
  for (const c of cuentasDormidas()) {
    if (tokenCaducado(c.token)) continue
    try {
      const r = await f(base + '/auth/renovar', { method: 'POST', headers: { Authorization: 'Bearer ' + c.token, Accept: 'application/json' } })
      if (!r || !r.ok) continue
      const d = await r.json().catch(() => null)
      if (d && d.renovado === true && d.token) { actualizarDormida(c.id, { token: d.token }); n++ }
    } catch (_) { /* sin red: la próxima vez */ }
  }
  return n
}

// El nombre de cada cuenta (su clínica, si es la de Biango), preguntado con SU token: las que se abrieron antes de que el
// inicio de sesión lo dijera no lo traen. Solo lo de esa cuenta (GET /auth/esta-cuenta).
export async function ponerAlDiaLosNombres({ base = '/api', _fetch } = {}) {
  const f = _fetch || (typeof fetch === 'function' ? fetch : null)
  if (!f) return 0
  let n = 0
  for (const c of cuentasDormidas()) {
    if (tokenCaducado(c.token) || (c.user && typeof c.user.cuenta_de_plataforma === 'boolean')) continue
    try {
      const r = await f(base + '/auth/esta-cuenta', { headers: { Authorization: 'Bearer ' + c.token, Accept: 'application/json' } })
      if (!r || !r.ok) continue
      const d = await r.json().catch(() => null)
      if (d && idDe(d.id) === idDe(c.id)) {
        actualizarDormida(c.id, { user: { clinica_nombre: d.clinica_nombre || null, cuenta_de_plataforma: d.cuenta_de_plataforma === true } })
        n++
      }
    } catch (_) { /* la próxima vez */ }
  }
  return n
}

// ── UNA PESTAÑA, UNA CUENTA ───────────────────────────────────────────────────────────────────────────────────────────
// La cuenta con la que arrancó (o entró) ESTA pestaña. Si otra pestaña cambia de cuenta, las peticiones de esta llevarían
// el token de la otra con la pantalla de la primera: services/api lo mira antes de cada petición y recarga.
let _deEstaPestana = (() => { const a = cuentaActiva(); return a ? a.id : null })()
export function cuentaDeEstaPestana() { return _deEstaPestana }
export function fijarCuentaDeEstaPestana(id) { _deEstaPestana = idDe(id) }
// ¿Ha cambiado la cuenta del almacén por debajo de esta pestaña? (las dos conocidas y distintas, y con varias cuentas en el
// dispositivo: es la única forma de cambiar de cuenta sin pasar por la pantalla de entrar. Sin el baúl, todo como siempre:
// entrar con otra cuenta en otra pestaña sigue el camino de toda la vida, el de la pantalla de entrar y su recarga.)
// `siempre`: sin mirar el baúl (lo que usa el aviso entre pestañas de components/CuentasGlobales, que solo salta en las OTRAS).
export function cambioLaCuentaPorDebajo({ siempre = false } = {}) {
  const a = cuentaActiva()
  if (!(_deEstaPestana && a && a.id !== _deEstaPestana)) return false
  if (siempre) return true
  try { const s = almacen(); return !!(s && s.getItem(CLAVE_CUENTAS)) } catch (_) { return false }
}
